import {
  parseRoundFeed,
  parseCandleFeed,
  parseRoundHistory,
  parseTickFeed,
  type RoundFeed,
  type CandleFeed,
  type Round,
  type TickFeed,
} from './model';
import { afterTickFailure, afterTickFetch, nextTickFile, type TickFetchState } from './poll-schedule';

/** Rounds feed plus `serverDate`: the response `Date` header in epoch seconds, if present and valid. */
export type RoundFetch = RoundFeed & { serverDate?: number };
export interface ShortMarketSource {
  rounds(signal: AbortSignal): Promise<RoundFetch>;
  candles(roundId: string, signal: AbortSignal): Promise<CandleFeed>;
  /** 1s tick feed: ticks.json, or ticks-tail.json with `tail`. Optional: without it the page draws candles only. */
  ticks?(signal: AbortSignal, options: { tail: boolean }): Promise<TickFeed>;
  /** A resolved round's 1s line: ticks/<roundId>.json, written once (BON-276). Optional, like `ticks`. */
  history?(round: Round, signal: AbortSignal): Promise<TickFeed>;
}
export const createShortMarketSource = (base: string): ShortMarketSource => {
  // Static JSON files served same-origin (nginx); no browser calls to an unconfirmed price provider.
  if (!base.startsWith('/') || base.startsWith('//') || /[\\?#\s]/.test(base))
    throw new Error('Short-market API must be a same-origin path');
  const root = base.replace(/\/$/, '');
  const get = async (path: string, signal: AbortSignal) => {
    const response = await fetch(`${root}${path}`, {
      signal,
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    // Not shown to users: the page renders its own translated notice on any failure.
    // `status` lets the poll tell a missing file (404) from other errors.
    if (!response.ok)
      throw Object.assign(new Error(`Short-market feed unavailable (HTTP ${response.status})`), {
        status: response.status,
      });
    return { body: await response.json(), date: response.headers?.get('Date') };
  };
  return {
    rounds: async signal => {
      const { body, date } = await get('/rounds.json', signal);
      const serverDate = date ? Date.parse(date) / 1000 : NaN;
      return Number.isFinite(serverDate) ? { ...parseRoundFeed(body), serverDate } : parseRoundFeed(body);
    },
    candles: async (id, signal) => parseCandleFeed((await get(`/candles/${encodeURIComponent(id)}.json`, signal)).body, id),
    ticks: async (signal, { tail }) => parseTickFeed((await get(tail ? '/ticks-tail.json' : '/ticks.json', signal)).body),
    history: async (round, signal) =>
      parseRoundHistory((await get(`/ticks/${encodeURIComponent(round.id)}.json`, signal)).body, round),
  };
};

/**
 * One tick read under the poll's file plan (`plan.current`, updated in place). A failed tail read is not an
 * outage: the full file is read in the same call (and only the full file from then on if the tail is missing).
 * `now` (the page clock, seconds) tells a stale tail, which makes the next read the full file.
 */
export const readTicks = async (
  ticks: NonNullable<ShortMarketSource['ticks']>,
  plan: { current: TickFetchState },
  signal: AbortSignal,
  now?: () => number
): Promise<TickFeed> => {
  const file = nextTickFile(plan.current);
  const sent = plan.current;
  try {
    const feed = await ticks(signal, { tail: file === 'tail' });
    if (!signal.aborted) {
      const stale = now !== undefined && now() - feed.updatedAt > feed.staleAfterSeconds;
      const next = afterTickFetch(plan.current, file, feed.points, stale);
      // The tab was hidden while the request was in flight: the return still reads the full file.
      plan.current = plan.current === sent ? next : { ...next, full: true };
    }
    return feed;
  } catch (error) {
    if (signal.aborted) throw error;
    plan.current = afterTickFailure(plan.current, file, error);
    if (file === 'tail') return readTicks(ticks, plan, signal, now);
    throw error;
  }
};
